import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TabBar from '@lib/components/TabBar/TabBar';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import { TabBarItem as TabBarItemType } from '@lib/components/TabBar/constants';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import { UseTeamQueriesResult } from 'routes/Teams/utils/useTeamQueries';
import { TeamViewMode } from '../enums';
import getTeamViewPageTabs from '../utils/getTeamViewPageTabs';
import { UseTeamActionsResult } from '../utils/useTeamActions';
import TeamViewDetails from './TeamViewDetails';
import TeamViewMembers from './TeamViewMembers';
import TeamViewDepartments from './TeamViewDepartments';

type Props = {
  useModalManagementResult: UseModalManagementResult;
  useTeamActionsResult: UseTeamActionsResult;
  useTeamQueriesResult: UseTeamQueriesResult;
  viewMode: TeamViewMode;
  isViewOnly: boolean;
};

export default function TeamViewPage({
  useModalManagementResult,
  useTeamActionsResult,
  useTeamQueriesResult,
  viewMode,
  isViewOnly,
}: Props) {
  const { t } = useTranslation();
  const {
    useUsersQueryHookResult,
    useTeamQueryHookResult,
    useTeamDepartmentsQueryHookResult,
  } = useTeamQueriesResult;
  const { onViewChange } = useTeamActionsResult;
  const { firstLoading } = useTeamQueryHookResult;
  const { allIds = [] } = useUsersQueryHookResult;
  const { totalCount: departmentsCounter } = useTeamDepartmentsQueryHookResult;

  const membersCounter = allIds.length;

  const tabs = useMemo(
    () => getTeamViewPageTabs({ t, membersCounter, departmentsCounter }),
    [t, membersCounter, departmentsCounter],
  );

  const handleActiveChange = useCallback(
    ({ value }: TabBarItemType) => onViewChange(value as TeamViewMode),
    [onViewChange],
  );

  return (
    <PageBody withoutHeader>
      {/* Outside the loading branch so the tabs are on screen from the first
          paint and stay mounted across switches — the sliding active indicator
          can't animate across a remount either. */}
      <TabBar
        items={tabs}
        active={viewMode}
        onActiveChange={handleActiveChange}
      />
      {firstLoading ? (
        <CircleLoader />
      ) : (
        <>
          {viewMode === TeamViewMode.ViewDetails && (
            <TeamViewDetails
              useModalManagementResult={useModalManagementResult}
              useTeamActionsResult={useTeamActionsResult}
              useTeamQueriesResult={useTeamQueriesResult}
              isViewOnly={isViewOnly}
            />
          )}
          {viewMode === TeamViewMode.ViewMembers && (
            <TeamViewMembers
              useModalManagementResult={useModalManagementResult}
              useTeamActionsResult={useTeamActionsResult}
              useTeamQueriesResult={useTeamQueriesResult}
              isViewOnly={isViewOnly}
            />
          )}
          {viewMode === TeamViewMode.ViewDepartments && (
            <TeamViewDepartments
              useTeamActionsResult={useTeamActionsResult}
              useTeamQueriesResult={useTeamQueriesResult}
              isViewOnly={isViewOnly}
            />
          )}
        </>
      )}
    </PageBody>
  );
}
